/**
 * Review queue — M6 §5 "Queue".
 *
 * Everything uploaded, filtered by status, one row each; clicking a row opens the review
 * modal over it. The table is the reviewer's whole working surface, so each column answers a
 * question they would otherwise have to open the modal to ask: what is it, which file, what
 * kind, did the checks pass, where is it in the flow, and how long has it been waiting.
 *
 * **The checks column is a judgement, not a count.** `2/3` in green and `2/3` in amber are
 * different situations — the first has an advisory note, the second is blocked — so the tally
 * carries the colour of `blockingOk`, and an upload whose checks have not run yet shows an em
 * dash rather than `0/0`, which would read as "everything failed".
 *
 * **The poll is conditional.** `pending` is the only status that changes without a reviewer
 * doing something (bytes still arriving, the validator still running), so the 10 s refetch
 * lives exactly as long as a pending row is on screen. A queue of decided rows is refreshed by
 * the decisions themselves (`ReviewModal` invalidates `['uploads']`), not by a timer.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchUploads, queryKeys, type UploadRow } from '../../lib/api';
import { EMDASH } from '../../lib/format';
import { Seg } from '../../components/Seg';
import {
  blockingOk,
  checkTally,
  fmtBytes,
  hasPending,
  QUEUE_FILTERS,
  QUEUE_POLL_MS,
  relativeTime,
  uploadTitle,
  type QueueFilter,
} from './manager';
import { ReviewModal } from './ReviewModal';

export function Queue() {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<QueueFilter>('all');
  const [open, setOpen] = useState<number | null>(null);

  // `all` sends no `status` at all rather than `status=all`: the parameter is a status name
  // server-side, and inventing a sixth one here would be a filter only this client believes in.
  const status = filter === 'all' ? null : filter;

  const { data, isError, isPending } = useQuery({
    queryKey: queryKeys.uploads(status),
    queryFn: fetchUploads,
    refetchInterval: (query) =>
      hasPending(query.state.data?.results ?? []) ? QUEUE_POLL_MS : false,
  });

  const rows = useMemo(() => data?.results ?? [], [data]);
  const selected = open === null ? null : (rows.find((r) => r.id === open) ?? null);

  const options = QUEUE_FILTERS.map((value) => ({
    value,
    label: t(`manager.queue.filter.${value}`),
    testId: `mgr-queue-filter-${value}`,
  }));

  return (
    <div className="card mgr-queue" data-testid="mgr-queue">
      <div className="card-head">
        <div className="card-head-text">
          <h3>{t('manager.queue.title')}</h3>
          <p className="sub">{t('manager.queue.sub')}</p>
        </div>
        <div className="mgr-queue-filter" data-testid="mgr-queue-filter">
          <Seg
            options={options}
            value={filter}
            onChange={setFilter}
            ariaLabel={t('manager.queue.filterLabel')}
          />
        </div>
      </div>

      {isError ? (
        <p className="rows-note" data-testid="mgr-queue-error">
          {t('manager.queue.error')}
        </p>
      ) : isPending ? (
        <p className="rows-note">{t('manager.queue.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="rows-note" data-testid="mgr-queue-empty">
          {t('manager.queue.empty')}
        </p>
      ) : (
        <div className="rows-wrap">
          <table className="mini rows-table mgr-queue-table">
            <thead>
              <tr>
                <th scope="col">{t('manager.queue.col.title')}</th>
                <th scope="col">{t('manager.queue.col.file')}</th>
                <th scope="col">{t('manager.queue.col.kind')}</th>
                <th scope="col">{t('manager.queue.col.checks')}</th>
                <th scope="col">{t('manager.queue.col.status')}</th>
                <th scope="col">{t('manager.queue.col.submitted')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <QueueRow key={row.id} row={row} onOpen={() => setOpen(row.id)} lang={i18n.language} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected ? <ReviewModal row={selected} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

interface QueueRowProps {
  row: UploadRow;
  onOpen: () => void;
  lang: string;
}

function QueueRow({ row, onOpen, lang }: QueueRowProps) {
  const { t } = useTranslation();
  const tally = checkTally(row);
  const when = relativeTime(row.uploaded_at, lang);

  return (
    <tr className="mgr-queue-row" data-testid={`mgr-queue-row-${row.id}`} onClick={onOpen}>
      <td>
        {/* The whole row is clickable, and this button is how a keyboard reaches it — a `tr`
            with a `tabindex` would be a tab stop announcing six cells and no action. */}
        <button
          type="button"
          className="mgr-queue-open"
          aria-label={t('manager.queue.open', { title: uploadTitle(row) })}
          onClick={onOpen}
        >
          {uploadTitle(row)}
        </button>
      </td>
      <td className="mgr-queue-file">
        <span>{row.filename}</span>
        <em>{fmtBytes(row.size_bytes)}</em>
      </td>
      <td>
        {row.kind ? t(`manager.kind.${row.kind}`) : EMDASH}
        {row.format ? ` · ${row.format.toUpperCase()}` : ''}
      </td>
      <td
        className={`mgr-queue-checks ${tally.total === 0 ? '' : blockingOk(row) ? 'ok' : 'warn'}`}
        data-testid={`mgr-queue-checks-${row.id}`}
      >
        {tally.total === 0 ? EMDASH : `${tally.passed}/${tally.total}`}
      </td>
      <td>
        <span className={`pill mgr-pill ${row.status}`} data-testid={`mgr-queue-status-${row.id}`}>
          {t(`manager.status.${row.status}`)}
        </span>
      </td>
      <td className="mgr-queue-when">
        <span>{when ?? EMDASH}</span>
        {/* The submitter the wizard asked for, falling back to the account that uploaded:
            they are usually the same person, but only the first one was *typed by* them. */}
        {row.submitter_name || row.uploaded_by ? (
          <em data-testid={`mgr-queue-by-${row.id}`}>
            {t('manager.queue.by', { user: row.submitter_name || row.uploaded_by })}
          </em>
        ) : null}
      </td>
    </tr>
  );
}

export default Queue;
